import React, { useRef, useEffect, useState } from 'react';
import * as THREE from 'three';
import './STLViewer.css';

const STLViewer = ({ 
  stlFile, 
  width = 400, 
  height = 400, 
  backgroundColor = 'transparent',
  modelColor = '#888888',
  autoRotate = false,
  autoRotateAxis = 'y',
  rotationSpeed = 0.01,
  enableMouseControls = true,
  lightIntensity = 1.0,
  cameraDistance = 5,
  initialRotationX = 0,
  initialRotationY = 0,
  initialRotationZ = 0
}) => {
  const mountRef = useRef(null);
  const sceneRef = useRef(null);
  const rendererRef = useRef(null);
  const cameraRef = useRef(null);
  const meshRef = useRef(null);
  const mouseRef = useRef({ x: 0, y: 0, isDown: false });
  const rotationRef = useRef({ x: 0, y: 0 });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // STL Loader function
  const loadSTL = (url) => {
    return new Promise((resolve, reject) => {
      const loader = new THREE.BufferGeometry();
      
      fetch(url)
        .then(response => {
          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }
          return response.arrayBuffer();
        })
        .then(buffer => {
          const geometry = parseSTL(buffer);
          resolve(geometry);
        })
        .catch(error => {
          reject(error);
        });
    });
  };

  // Simple STL parser
  const parseSTL = (buffer) => {
    const dataView = new DataView(buffer);
    const isASCII = isASCIISTL(buffer);
    
    if (isASCII) {
      return parseASCIISTL(new TextDecoder().decode(buffer));
    } else {
      return parseBinarySTL(dataView);
    }
  };

  const isASCIISTL = (buffer) => {
    const text = new TextDecoder().decode(buffer.slice(0, 1024));
    return text.toLowerCase().includes('solid');
  };

  const parseASCIISTL = (text) => {
    const geometry = new THREE.BufferGeometry();
    const vertices = [];
    const normals = [];
    
    const lines = text.split('\n');
    let currentNormal = null;
    
    for (let line of lines) {
      line = line.trim();
      
      if (line.startsWith('facet normal')) {
        const parts = line.split(' ');
        currentNormal = [
          parseFloat(parts[2]),
          parseFloat(parts[3]),
          parseFloat(parts[4])
        ];
      } else if (line.startsWith('vertex')) {
        const parts = line.split(' ');
        vertices.push(
          parseFloat(parts[1]),
          parseFloat(parts[2]),
          parseFloat(parts[3])
        );
        if (currentNormal) {
          normals.push(...currentNormal);
        }
      }
    }
    
    geometry.setAttribute('position', new THREE.Float32BufferAttribute(vertices, 3));
    geometry.setAttribute('normal', new THREE.Float32BufferAttribute(normals, 3));
    
    return geometry;
  };

  const parseBinarySTL = (dataView) => {
    const geometry = new THREE.BufferGeometry();
    
    // Skip header (80 bytes)
    const triangleCount = dataView.getUint32(80, true);
    
    const vertices = [];
    const normals = [];
    
    for (let i = 0; i < triangleCount; i++) {
      const offset = 84 + i * 50;
      
      // Normal vector
      const nx = dataView.getFloat32(offset, true);
      const ny = dataView.getFloat32(offset + 4, true);
      const nz = dataView.getFloat32(offset + 8, true);
      
      // Three vertices
      for (let j = 0; j < 3; j++) {
        const vertexOffset = offset + 12 + j * 12;
        vertices.push(
          dataView.getFloat32(vertexOffset, true),
          dataView.getFloat32(vertexOffset + 4, true),
          dataView.getFloat32(vertexOffset + 8, true)
        );
        normals.push(nx, ny, nz);
      }
    }
    
    geometry.setAttribute('position', new THREE.Float32BufferAttribute(vertices, 3));
    geometry.setAttribute('normal', new THREE.Float32BufferAttribute(normals, 3));
    
    return geometry;
  };

  // Initialize Three.js scene
  useEffect(() => {
    if (!mountRef.current) return;

    // Scene
    const scene = new THREE.Scene();
    if (backgroundColor !== 'transparent') {
      scene.background = new THREE.Color(backgroundColor);
    }
    sceneRef.current = scene;

    // Camera
    const camera = new THREE.PerspectiveCamera(75, width / height, 0.1, 1000);
    camera.position.z = cameraDistance;
    cameraRef.current = camera;

    // Renderer
    const renderer = new THREE.WebGLRenderer({ 
      antialias: true, 
      alpha: backgroundColor === 'transparent' 
    });
    renderer.setSize(width, height);
    renderer.shadowMap.enabled = true;
    renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    rendererRef.current = renderer;

    // Lighting
    const ambientLight = new THREE.AmbientLight(0x404040, 0.4);
    scene.add(ambientLight);

    const directionalLight = new THREE.DirectionalLight(0xffffff, lightIntensity);
    directionalLight.position.set(1, 1, 1);
    directionalLight.castShadow = true;
    scene.add(directionalLight);

    const directionalLight2 = new THREE.DirectionalLight(0xffffff, lightIntensity * 0.5);
    directionalLight2.position.set(-1, -1, -1);
    scene.add(directionalLight2);

    mountRef.current.appendChild(renderer.domElement);

    return () => {
      if (mountRef.current && renderer.domElement) {
        mountRef.current.removeChild(renderer.domElement);
      }
      renderer.dispose();
    };
  }, [width, height, backgroundColor, lightIntensity, cameraDistance]);

  // Load STL file
  useEffect(() => {
    if (!stlFile || !sceneRef.current) return;

    setLoading(true);
    setError(null);

    // Remove existing mesh
    if (meshRef.current) {
      sceneRef.current.remove(meshRef.current);
      meshRef.current = null;
    }

    loadSTL(stlFile)
      .then(geometry => {
        geometry.computeBoundingBox();
        geometry.computeVertexNormals();

        // Center the geometry
        const box = geometry.boundingBox;
        const center = box.getCenter(new THREE.Vector3());
        geometry.translate(-center.x, -center.y, -center.z);

        // Scale to fit
        const size = box.getSize(new THREE.Vector3());
        const maxDim = Math.max(size.x, size.y, size.z);
        const scale = 2 / maxDim;
        geometry.scale(scale, scale, scale);

        // Create material and mesh
        const material = new THREE.MeshLambertMaterial({ 
          color: modelColor,
          side: THREE.DoubleSide
        });
        
        const mesh = new THREE.Mesh(geometry, material);
        mesh.castShadow = true;
        mesh.receiveShadow = true;
        
        // Apply initial rotation
        mesh.rotation.x = initialRotationX;
        mesh.rotation.y = initialRotationY;
        mesh.rotation.z = initialRotationZ;
        
        // Update rotation ref to match initial rotation
        rotationRef.current.x = initialRotationX;
        rotationRef.current.y = initialRotationY;
        
        sceneRef.current.add(mesh);
        meshRef.current = mesh;
        setLoading(false);
      })
      .catch(err => {
        console.error('Error loading STL:', err);
        setError(err.message);
        setLoading(false);
      });
  }, [stlFile, modelColor, initialRotationX, initialRotationY, initialRotationZ]);

  // Mouse controls
  useEffect(() => {
    if (!enableMouseControls || !mountRef.current) return;

    const handleMouseDown = (event) => {
      mouseRef.current.isDown = true;
      mouseRef.current.x = event.clientX;
      mouseRef.current.y = event.clientY;
    };

    const handleMouseUp = () => {
      mouseRef.current.isDown = false;
    };

    const handleMouseMove = (event) => {
      if (!mouseRef.current.isDown || !meshRef.current) return;

      const deltaX = event.clientX - mouseRef.current.x;
      const deltaY = event.clientY - mouseRef.current.y;

      rotationRef.current.y += deltaX * 0.01;
      rotationRef.current.x += deltaY * 0.01;

      // Limit vertical rotation
      rotationRef.current.x = Math.max(-Math.PI / 2, Math.min(Math.PI / 2, rotationRef.current.x));

      meshRef.current.rotation.x = rotationRef.current.x;
      meshRef.current.rotation.y = rotationRef.current.y;

      mouseRef.current.x = event.clientX;
      mouseRef.current.y = event.clientY;
    };

    const element = mountRef.current;
    element.addEventListener('mousedown', handleMouseDown);
    element.addEventListener('mouseup', handleMouseUp);
    element.addEventListener('mousemove', handleMouseMove);
    element.addEventListener('mouseleave', handleMouseUp);

    return () => {
      element.removeEventListener('mousedown', handleMouseDown);
      element.removeEventListener('mouseup', handleMouseUp);
      element.removeEventListener('mousemove', handleMouseMove);
      element.removeEventListener('mouseleave', handleMouseUp);
    };
  }, [enableMouseControls]);

  // Animation loop
  useEffect(() => {
    let animationId;

    const animate = () => {
      if (autoRotate && meshRef.current) {
        switch (autoRotateAxis.toLowerCase()) {
          case 'x':
            meshRef.current.rotation.x += rotationSpeed;
            break;
          case 'z':
            meshRef.current.rotation.z += rotationSpeed;
            break;
          case 'y':
          default:
            meshRef.current.rotation.y += rotationSpeed;
            break;
        }
      }

      if (rendererRef.current && sceneRef.current && cameraRef.current) {
        rendererRef.current.render(sceneRef.current, cameraRef.current);
      }

      animationId = requestAnimationFrame(animate);
    };

    animate();

    return () => {
      if (animationId) {
        cancelAnimationFrame(animationId);
      }
    };
  }, [autoRotate, autoRotateAxis, rotationSpeed]);

  return (
    <div className="stl-viewer-container" style={{ width, height }}>
      <div ref={mountRef} className="stl-viewer-mount" />
      {loading && (
        <div className="stl-viewer-loading">
          <div className="loading-spinner"></div>
          <p>Loading 3D model...</p>
        </div>
      )}
      {error && (
        <div className="stl-viewer-error">
          <p>Error loading STL file:</p>
          <p>{error}</p>
        </div>
      )}
      {enableMouseControls && !loading && !error && (
        <div className="stl-viewer-controls">
          <p>Click and drag to rotate</p>
        </div>
      )}
    </div>
  );
};

export default STLViewer;
